import React, { useId } from 'react';
import { TypedMap } from 'flow/types/TypedMap';
import { ContactFull } from 'flow/types/Contact';
import { applyLink } from 'flow/utils/message-utils';
import useAppContext from 'flow/hooks/useAppContext';
import CallAdaptiveButton from 'flow/components/CallAdaptiveButton';
import MailToWrapper from 'flow/components/custom-components/MailToWrapper';
import Icon from 'flow/components/ui-components/Icon';
import NumberWrapper from 'flow/components/ui-components/NumberWrapper';
import { ContactCardField } from './buildContactFields';

interface Props {
    field: ContactCardField;
    contact?: TypedMap<ContactFull>;
}

interface ValueProps {
    value: string;
    target: string;
    contact?: TypedMap<ContactFull>;
}

// The value hugs its text; the icon sits at the row's end, positioned inside
// the control so it stays part of the click target while the space between
// the two belongs to nothing. The row keeps the icon's width free (pe-8) so
// a long value wraps instead of running under it.
const valueClassName = 'min-w-0 text-base leading-6 wrap-break-word';
// A 32px icon box centred on the 24px first line, for both rows.
const iconBoxClassName =
    'absolute end-0 -top-1 flex size-8 items-center justify-center text-gray-600';

/**
 * A number with its call button. The number is the button's <label>
 * (`htmlFor`), so a click on it activates the button natively; no handler,
 * and the button stays the row's only focusable control. Its `aria-label`
 * keeps "Call" as its name. When CallAdaptiveButton renders nothing (calling
 * is not possible) the label points at nothing and the number is plain
 * text: no pointer, no hover colour, no click.
 */
const NumberValue = ({ value, target, contact }: ValueProps) => {
    const buttonId = useId();

    return (
        <label
            htmlFor={buttonId}
            className="has-[button:enabled]:hover:text-link inline-flex max-w-full cursor-default text-gray-900 has-[button:enabled]:cursor-pointer"
        >
            <span className={valueClassName}>
                <NumberWrapper>{value}</NumberWrapper>
            </span>
            {/* CallAdaptiveButton applies the call guards (Teams, no phone,
             *  outgoing calls disabled, pending transfer) and turns into a
             *  transfer button while in a call. cursor-pointer: the transfer
             *  variant's IconButton has none of its own. */}
            <span className={iconBoxClassName}>
                <CallAdaptiveButton
                    id={buttonId}
                    phoneNumber={target}
                    transferContact={contact}
                    size="small"
                    inactiveBgColor="bg-transparent"
                    className="cursor-pointer"
                />
            </span>
        </label>
    );
};

/**
 * An email address inside its mailto link, with the icon at the row's end.
 * The link is named by its visible text, the address (WCAG 2.5.3); the icon
 * adds "Send email" to that name. The link's box, and so its focus ring, is
 * the address.
 */
const EmailValue = ({ value, target }: ValueProps) => {
    const { i18next } = useAppContext();

    return (
        <MailToWrapper
            recipient={target}
            className="focus-ring hover:text-link inline-flex max-w-full rounded-sm text-gray-900"
        >
            <span className={valueClassName}>{value}</span>
            <span className={iconBoxClassName}>
                <Icon
                    iconKey="email"
                    size="1.5rem"
                    label={i18next.t('Tooltip.Title.SendEmail')}
                />
            </span>
        </MailToWrapper>
    );
};

const ContactDetailRow = ({ field, contact }: Props) => {
    const { label, value, action } = field;

    const renderValue = () => {
        if (action?.kind === 'call') {
            return (
                <NumberValue
                    value={value}
                    target={action.target}
                    contact={contact}
                />
            );
        }
        if (action?.kind === 'mail') {
            return <EmailValue value={value} target={action.target} />;
        }
        // Plain text turns its URLs into links, as ContactField does
        // elsewhere.
        return (
            <span className={`${valueClassName} text-gray-900`}>
                {applyLink(value)}
            </span>
        );
    };

    return (
        <div className="flex w-full flex-col">
            <span className="text-xs leading-6 text-gray-500">{label}</span>
            {/* The row's own line height, so the icon box is centred on the
             *  first line whatever the card inherits. */}
            <div
                className={
                    action ? 'relative pe-8 text-base leading-6' : undefined
                }
            >
                {renderValue()}
            </div>
        </div>
    );
};

export default React.memo(ContactDetailRow);
